import { DisplayValue } from '@adaptiva/core/common';
import { SHORTCUT_KEYBIND, ShortcutService } from '@adaptiva/core/keyboard';
import { OverlayService } from '@adaptiva/core/overlays';
import { SnackbarService } from '@adaptiva/core/snackbars';
import { TableSortService } from '@adaptiva/core/tables';
import { UiService } from '@adaptiva/core/ui';
import { BaseWidgetComponent, WidgetService } from '@adaptiva/core/widgets';
import { AuthenticationService, EEditorErrorType, EJavaClassNames, GlobalEditorService, IEditorComponent, IEditorInput } from '@adaptiva/data-service';
import { BlacklistPayload, HousekeepingProperties, MetadataAuthoringManagerService, MetadataObject, MetadataPropertyCopyService, MetadataSerialization } from '@adaptiva/metadata/common';
import { OverlayComponent } from '@adaptiva/widgets/overlay';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { BaseMetadataEditorComponent } from '../base-metadata-editor';
import { AuthoringToolbarAuthoringMenucontroller } from './menus';
import { AuthoringToolbarApprovingMenuController } from './menus/authoring-toolbar-approving-menu-controller';

@Component({
	standalone: false,
	selector: 'ada-authoring-toolbar',
	templateUrl: 'authoring-toolbar.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrls: ['authoring-toolbar.component.scss'],
	providers: [
		AuthoringToolbarAuthoringMenucontroller,
		AuthoringToolbarApprovingMenuController,
		ShortcutService,
		TableSortService,
		{ provide: SHORTCUT_KEYBIND, useValue: { keyName: 'k', modifiers: ['ctrlKey'] } },
	]
})
export class AuthoringToolbarComponent extends BaseWidgetComponent implements OnInit {

	private _classLogName: string = 'AuthoringToolbarComponent';

	@Input()
	public currentObject$: BehaviorSubject<MetadataObject>;

	@Input()
	public saveButtonEnabled: BehaviorSubject<boolean>;

	@Input()
	public objectClassname: EJavaClassNames;

	@Input()
	public createChildOptions: DisplayValue[];

	@Input()
	public canComplete: (_errorType: EEditorErrorType) => Observable<boolean>;

	@Output()
	public saveAndComplete: EventEmitter<boolean> = new EventEmitter();

	@Output()
	public propertiesPasted: EventEmitter<MetadataObject> = new EventEmitter();

	@ViewChild('CommentOverlay')
	public commentOverlay: OverlayComponent;

	@ViewChild('KeyboardShortcutDisplay')
	public shortcutDisplay: BaseWidgetComponent;

	private _currentObjectId: number;

	// AuthoringProperties
	public isCheckedOut$: BehaviorSubject<boolean> = new BehaviorSubject(false);

	// Permissions
	public isApprover: boolean = false;

	public isAuthor: boolean = false;

	// Comments:
	public _rejectionComment: string;

	public _callback: Function;

	public blacklistPayload: BlacklistPayload = new BlacklistPayload();

	constructor(
		public copyService: MetadataPropertyCopyService,
		public metadataAuthoring: MetadataAuthoringManagerService,
		public ui: UiService,
		protected approvingMenuController: AuthoringToolbarApprovingMenuController<MetadataObject>,
		protected authoringMenuController: AuthoringToolbarAuthoringMenucontroller<MetadataObject>,
		private _authentication: AuthenticationService,
		private _globalEditorService: GlobalEditorService,
		private _overlayService: OverlayService,
		private _shortcutService: ShortcutService,
		private _snackbars: SnackbarService,
		_widget: WidgetService,
	) {
		super(_widget);
		// Load permissions:
		this.metadataAuthoring.isApprover().pipe(take(1)).subscribe({
			next: (_isApprover: boolean) => {
				this.isApprover = _isApprover;
				this._cd.markForCheck();
			}
		});

		this.metadataAuthoring.isAuthor().pipe(take(1)).subscribe({
			next: (_isAuthor: boolean) => {
				this.isAuthor = _isAuthor;
				this._cd.markForCheck();
			}
		});
	}

	public override ngOnInit(): void {
		super.ngOnInit();
		this.authoringMenuController.propertiesPasted = this.propertiesPasted;
		this.authoringMenuController.saveAndComplete = this.saveAndComplete;
		this.authoringMenuController.setOnCurrentObject(this.currentObject$);
		this.authoringMenuController.isSaveButtonEnabled = this.saveButtonEnabled;

		// Monitor for new objects:
		this.currentObject$.pipe(
			filter((_object: MetadataObject) => !!_object),
			takeUntil(this.destroyed$)
		).subscribe({
			next: (_object: MetadataObject) => {
				this.authoringMenuController.clear();
				this.approvingMenuController.clear();

				const _isCheckedOut: boolean = MetadataSerialization.getPropertyValue<boolean, HousekeepingProperties>(
					_object, 'Housekeeping', 'CheckedOutState'
				);

				this.isCheckedOut$.next(_isCheckedOut);
				this._currentObjectId = _object.objectId;
			}
		});

		this._setupKeyListeners();
	}

	/**
	 * Saves the current editor object
	 * @param checkIn 
	 */
	public saveMetadataObject(): void {
		this._globalEditorService.editorInputChange.pipe(
			take(1),
			map((_input: IEditorInput) => {
				return this._globalEditorService.getEditorComponent<MetadataObject>(_input.editorId);
			}),
			take(1)
		).subscribe({
			next: (_editorComponent: IEditorComponent<MetadataObject>) => {
				(_editorComponent as BaseMetadataEditorComponent<MetadataObject>).saveMetadataObject();
			}
		});
	}

	public saveMetadataObjectAndComplete(): void {
		this.saveAndComplete.emit(true);
		this.saveMetadataObject();
	}

	public checkItem(): void {
		this.isCheckedOut$.pipe(
			take(1),
			switchMap(
				(_checkIn: boolean) => {
					let _request: Observable<null>;

					if (_checkIn)
						_request = this.metadataAuthoring.checkInObjects([this._currentObjectId]);
					else
						_request = this.metadataAuthoring.checkOutObjects([this._currentObjectId]);

					return this._authentication.user.pipe(
						take(1),
						switchMap(_user => this.currentObject$.pipe(
							take(1),
							switchMap(_object => {
								const _checkedOut: boolean = MetadataSerialization.getPropertyValue<boolean>(
									_object, 'Housekeeping', 'CheckedOutState'
								);
								const _checkedOutBy: string = MetadataSerialization.getPropertyValue<string>(
									_object, 'Housekeeping', 'CheckedOutBy'
								);

								if (_checkedOut && _checkedOutBy !== _user.loginId) {
									return this._overlayService.confirm(
										'Are you sure?',
										`${_object.name} is currently checked out by ${_checkedOutBy}. Are you sure you want to ` +
										'take over this object?'
									).pipe(
										switchMap(_confirmed => {
											if (_confirmed)
												return _request;
											else
												return EMPTY;
										})
									);
								}

								return _request;
							})
						)),
						take(1)
					);
				}
			)
		).subscribe({
			next: () => {
				const _message: string = `Check ${this.isCheckedOut$.value ? 'in' : 'out'} successful`;

				this._snackbars.display(_message);
			},
			error: (_error) => {
				const _methodName: string = '.checkItem';

				this._widgetService.logging.error(`${this._classLogName}${_methodName}`, _error)();
			}
		});
	}

	public approveForPublication(_rejectionComment: string, _approveOrReject: boolean): void {
		this.metadataAuthoring.reviewForPublication(_approveOrReject, [this._currentObjectId], _rejectionComment).subscribe({
			error: (_error) => {
				const _methodName: string = '.approveForPublication';

				this._widgetService.logging.error(`${this._classLogName}${_methodName}`, _error)();
			}
		});
	}

	public approveForDeletion(_rejectionComment: string, _approveOrReject: boolean): void {
		this.metadataAuthoring.reviewForDeletion(_approveOrReject, this._currentObjectId, _rejectionComment).subscribe({
			error: (_error) => {
				const _methodName: string = '.approveForDeletion';

				this._widgetService.logging.error(`${this._classLogName}${_methodName}`, _error)();
			}
		});
	}

	public withdrawApprovalsForPublication(_rejectionComment: string): void {
		this.metadataAuthoring.withdrawApprovalsForPublication(_rejectionComment, [this._currentObjectId]).subscribe({
			error: (_error) => {
				const _methodName: string = '.withdrawApprovalsForPublication';

				this._widgetService.logging.error(`${this._classLogName}${_methodName}`, _error)();
			}
		});
	}

	// #region Rejection Commenting:
	public setComment(_comment: string): void {
		this._rejectionComment = _comment;
	}

	/**
	 * Set comment completion information
	 * @param _callback The function to be called after completion
	 * @param _params Any parameters passed to the callback
	 */
	public openCommentInput(_callback: Function, ..._params: any[]): void {
		// Set initial info when opening, means we do not need to clear this later.
		this._callback = ((_rejectionComment: string) => _callback.call(this, _rejectionComment, ..._params));
		this._rejectionComment = '';

		this.commentOverlay.open();
	}

	public submitCommentedResponse() {
		this._callback(this._rejectionComment);
	}
	// #endregion

	public keybindInfoClick(_event: MouseEvent): void {
		_event.stopPropagation();
		this._shortcutService.showShortcutInfo();
	}

	private _setupKeyListeners(): void {
		this._shortcutService.addShortcut(
			'Check In/Out',
			{ keyName: 'c' },
			() => {
				const _message: string = `Checking ${this.isCheckedOut$.value ? 'in' : 'out'} ...`;

				this._snackbars.display(_message);
				this.checkItem();
			},
			() => this.currentObject$.pipe(
				take(1),
				switchMap((_object: MetadataObject) => {
					const _request: Observable<boolean> = _object.isCheckedOut()
						? this.metadataAuthoring.isCheckInAllowed([_object])
						: this.metadataAuthoring.isCheckOutAllowed([_object.objectId]);

					return this.saveButtonEnabled.pipe(take(1), switchMap((_isEnabled: boolean) => {
						if (!_isEnabled)
							return _request;

						return of(false);
					}));
				}),
				take(1),
			)
		);

		this._shortcutService.addShortcut(
			'Approve for Publication',
			{ keyName: 'a' },
			() => {
				const _message: string = 'Approving for Publication';

				this._snackbars.display(_message);
				this.openCommentInput(this.approveForPublication, false);
			},
			() => this.currentObject$.pipe(
				take(1),
				switchMap((_object: MetadataObject) => {
					return this.metadataAuthoring.isApproveForPublicationAllowed(true, [_object]);
				})
			)
		);

		this._shortcutService.addShortcut(
			'Reject for Publication',
			{ keyName: 'd' },
			() => {
				const _message: string = 'Rejecting for Publication';

				this._snackbars.display(_message);
				this.openCommentInput(this.withdrawApprovalsForPublication);
			},
			() => this.currentObject$.pipe(
				take(1),
				switchMap((_object: MetadataObject) => {
					return this.metadataAuthoring.isWithdrawApprovalForPublicationAllowed([_object]);
				})
			)
		);

		this._shortcutService.addShortcut(
			'Approve for Deletion',
			{ keyName: 'a', modifiers: ['ctrlKey'] },
			() => {
				const _message: string = 'Approving for Deletion';

				this._snackbars.display(_message);
				this.approveForDeletion('', true);
			},
			() => this.currentObject$.pipe(
				take(1),
				switchMap((_object: MetadataObject) => {
					return this.metadataAuthoring.isApproveForDeletionAllowed(_object);
				})
			)
		);

		this._shortcutService.addShortcut(
			'Reject for Deletion',
			{ keyName: 'd', modifiers: ['ctrlKey'] },
			() => {
				const _message: string = 'Rejecting for Deletion';

				this._snackbars.display(_message);
				this.openCommentInput(this.approveForDeletion, false);
			},
			() => this.currentObject$.pipe(
				take(1),
				switchMap((_object: MetadataObject) => {
					return this.metadataAuthoring.isApproveForDeletionAllowed(_object);
				})
			)
		);

		this._shortcutService.addShortcut(
			'Save',
			{ keyName: 's' },
			() => {
				const _message: string = 'Save';

				this._snackbars.display(_message);
				this.saveMetadataObject();
			},
			() =>
				this.currentObject$.pipe(
					take(1),
					switchMap((_object: MetadataObject) => {
						return this.metadataAuthoring.canEdit(_object);
					})
				)
		);

		this._shortcutService.addShortcut(
			'Save and Complete',
			{ keyName: 's', modifiers: ['ctrlKey'] },
			() => {
				const _message: string = 'Save and Complete';

				this._snackbars.display(_message);
				this.saveMetadataObjectAndComplete();
			},
			() => this.currentObject$.pipe(
				take(1),
				switchMap((_object: MetadataObject) => {
					return this.metadataAuthoring.isCompleteAllowed(_object);
				})
			)
		);
	}

}
